/**
 * flashbookReadHistory.ts — a learner's reads of one book, as a progression.
 *
 * The player already keeps every read that ended (`#/lib/flashbookProgress`), and
 * the report already says *which* read this is — "3 reads of this book". That is a
 * count, not a story. A learner who has read a book three times wants to know
 * whether the third was better than the first, and by how much, and whether they
 * have been getting better or sliding; a number of reads answers none of that.
 *
 * So this file turns the register into the shape that question has: one row per
 * read in the order they happened, each with its score and its change from the one
 * before, plus the handful of facts a summary line can be built from. It computes
 * nothing the register does not already hold — the register is the record and this
 * is only how it reads.
 *
 * Four decisions shape it.
 *
 *  - **Oldest first, numbered from the end.** The register stores newest first,
 *    because that is the order a resumption wants; a progression is read the other
 *    way, so the rows are reversed. Numbering then counts *back* from the total, so
 *    a learner who has read a book twenty-five times sees `Read 25` on the newest
 *    row even though the register only keeps the last twenty — a numbering that
 *    counted the rows on screen would relabel every read each time the list trims,
 *    and would tell them their third read was their first.
 *  - **Percentages, never points.** The book can be edited between two reads, so
 *    the points on offer change: comparing raw points across reads would report a
 *    drop that is really a longer book. Every comparison here is in points of
 *    percentage, which is the only unit two readings of a book share.
 *  - **A book with nothing to score has no percentage.** A reading-only book — or a
 *    book whose exercises were all added after this read — offers no points, and
 *    there is no honest percentage to print for it; a `0%` there would be a claim
 *    about the learner rather than about the book they were handed. Such a read is
 *    listed with `pct: null`, left out of the series, and skipped over when working
 *    out what the *next* read improved on, so a scoreless read cannot drag a real
 *    score down. Note the contrast, because it is deliberate: a read of a book
 *    that *did* offer points and earned none is `0%`, not `null` — the learner was
 *    given questions and got them wrong, or did not answer them, and the cover's
 *    own best score is computed the same way, so the two can never disagree.
 *  - **A practice is not a read.** Practices are registered beside the reads (see
 *    `#/lib/flashbookProgress`) and are counted separately, never as rows: a
 *    learner who came back for the eight questions a report named has not read the
 *    book again, and a chart of their reads must not count it as one.
 */
import {type FlashBookReadProgress} from '#/lib/flashbookProgress'

/** One read of the book, as the learner's own history shows it. */
export type ReadHistoryRow = {
  /**
   * Which read of the book this was, counting every read ever ended.
   *
   * Counted from the total rather than from the rows, so the number is the same
   * fact however many of them the register still holds (see the module note).
   */
  number: number
  id: string
  /** When this read ended, for a date beside it. */
  endedAt: string
  /** Reached the end of the book, rather than being set down part-way. */
  completed: boolean
  /** How far it got, 1-based, so it can be printed as `page 7`. */
  reachedPage: number
  /** How long the book was when this read happened. */
  pageCount: number
  answered: number
  points: number
  maxPoints: number
  /** `null` only when the book offered no points at all — see the module note. */
  pct: number | null
  timeMs: number
  /**
   * The read the stored best score belongs to.
   *
   * The *earliest* read matching it, because that is what the register credits:
   * a later read that only matches an existing best does not replace it, so the
   * cover's "best 58%" and this marker can never disagree.
   */
  best: boolean
  /**
   * Points of percentage better or worse than the previous read that scored.
   *
   * `null` for the first scored read — there is nothing to have improved on — and
   * for every scoreless read, which is left out of the chain rather than counted
   * as a fall.
   */
  delta: number | null
}

export type FlashBookReadHistory = {
  /** Ended reads the register still holds, oldest first. */
  reads: ReadHistoryRow[]
  /** Reads that ever ended, including any the register has since dropped. */
  totalReads: number
  /** How many of the reads above reached the end of the book. */
  completed: number
  /** Practices sat on this book (listed, never rows — see the module note). */
  practices: number
  /** Reads that produced a percentage. */
  scored: number
  /** The best percentage ever earned, or `null` when nothing was scored. */
  bestPct: number | null
  /** The first scored read's percentage, for "since your first". */
  firstPct: number | null
  /** The most recent scored read's percentage. */
  lastPct: number | null
  /** `lastPct - firstPct`, or `null` when there is only one scored read. */
  improvement: number | null
  /**
   * How many reads in a row got better, counting back from the most recent scored
   * read. 1 means the last read was an improvement on the one before it.
   */
  improvingRun: number
  /** The scored percentages, oldest first: the shape of the line to draw. */
  series: number[]
  /** Time spent reading this book across every read still listed. */
  totalTimeMs: number
}

/**
 * The percentage of a score, or `null` when the book offered nothing to score.
 *
 * Takes the pair rather than a session because both a read and the register's own
 * `best` are such a pair — and it is deliberately the register's own arithmetic
 * (see `#/lib/flashbookProgress`), so the number on a row is the number the cover
 * would print for the same read.
 */
function pctOf(score: {points: number; maxPoints: number}): number | null {
  if (score.maxPoints <= 0) return null
  return Math.round((score.points / score.maxPoints) * 100)
}

/**
 * The learner's history of a book, or `null` when there is nothing to show.
 *
 * `null` for a book nobody has finished reading, because a history of one read is
 * the report the learner is already looking at — the section it feeds only says
 * something once there is a second reading to compare against.
 */
export function buildFlashBookReadHistory(
  progress: FlashBookReadProgress | null | undefined,
): FlashBookReadHistory | null {
  if (!progress) return null

  const practices = progress.sessions.filter(
    session => session.kind === 'practice',
  ).length
  const ended = progress.sessions
    .filter(session => session.kind !== 'practice')
    // The register holds these newest first; a progression is read the other way.
    .slice()
    .sort((a, b) => Date.parse(a.endedAt) - Date.parse(b.endedAt))

  if (ended.length === 0) return null

  const best = progress.totals.best
  const totalReads = Math.max(progress.totals.reads, ended.length)
  /** The first read the store credits as the best, so ties keep the earliest. */
  let bestRowId: string | null = null
  if (best) {
    const earliest = ended.find(
      session =>
        session.points === best.points && session.maxPoints === best.maxPoints,
    )
    bestRowId = earliest?.id ?? null
  }

  const reads: ReadHistoryRow[] = ended.map((session, index) => ({
    // Counted back from the end, so the newest read is always the last number.
    number: Math.max(1, totalReads - (ended.length - 1 - index)),
    id: session.id,
    endedAt: session.endedAt,
    completed: session.completed,
    reachedPage: Math.max(1, session.pageReached + 1),
    pageCount: Math.max(session.pageCount, session.pageReached + 1),
    answered: session.answered,
    points: session.points,
    maxPoints: session.maxPoints,
    pct: pctOf(session),
    timeMs: session.timeMs,
    best: session.id === bestRowId,
    delta: null,
  }))

  /*
   * The deltas, in a second pass over the rows in order.
   *
   * Each scored read is compared with the previous *scored* one, so a sitting that
   * scored nothing — a book of reading, a read abandoned on the first page — sits
   * in the list without breaking the chain: the read after it is measured against
   * the last one that actually produced a number, rather than against a zero it
   * never earned.
   */
  let previous: number | null = null
  let improvingRun = 0
  const series: number[] = []
  for (const row of reads) {
    if (row.pct === null) continue
    row.delta = previous === null ? null : row.pct - previous
    if (row.delta !== null && row.delta > 0) improvingRun += 1
    else if (row.delta !== null) improvingRun = 0
    series.push(row.pct)
    previous = row.pct
  }

  const scoredRows = reads.filter(row => row.pct !== null)
  const firstPct = scoredRows[0]?.pct ?? null
  const lastPct = scoredRows[scoredRows.length - 1]?.pct ?? null

  return {
    reads,
    totalReads,
    completed: reads.filter(row => row.completed).length,
    practices,
    scored: scoredRows.length,
    bestPct: pctOf(progress.totals.best ?? {points: 0, maxPoints: 0}),
    firstPct,
    lastPct,
    improvement:
      firstPct === null || lastPct === null || scoredRows.length < 2
        ? null
        : lastPct - firstPct,
    improvingRun,
    series,
    totalTimeMs: reads.reduce((total, row) => total + row.timeMs, 0),
  }
}
